/**
 * CSV parsing and import helpers for Cibles.
 * No React — pure browser/TypeScript logic only.
 */
import { TARGET_LIMITS } from '@traaker/shared';
import type { TargetRow } from './CATEGORIES';

// ---------------------------------------------------------------------------
// Import error
// ---------------------------------------------------------------------------

/**
 * An import failure the UI can translate. Carries the i18n KEY, not a rendered
 * string, so the message follows the active UI language and stays assertable in a
 * test without pulling i18next in.
 */
export class ImportError extends Error {
  constructor(public readonly key: string) {
    super(key);
  }
}

// ---------------------------------------------------------------------------
// Gzip helpers
// ---------------------------------------------------------------------------

/** Gzip raw bytes with the browser's native CompressionStream. */
export async function gzipBytes(input: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('gzip');
  const writer = cs.writable.getWriter();
  writer.write(input);
  writer.close();
  const chunks: Uint8Array[] = [];
  const reader = cs.readable.getReader();
  let done = false;
  while (!done) {
    const { value, done: d } = await reader.read();
    if (value) chunks.push(value);
    done = d;
  }
  const totalLen = chunks.reduce((s, c) => s + c.length, 0);
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.length; }
  return result;
}

/** Gzip member header — 0x1f 0x8b (RFC 1952 §2.3.1). */
export function isGzipped(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

/**
 * Bytes to PUT for an import — ALWAYS gzipped.
 *
 * The staging key ends `.ndjson.gz` and `_run_import` gunzips it, so an uncompressed
 * body raises BadGzipFile inside the worker AFTER the commit has taken `importJob`:
 * the lock then blocks every write on the brand until its 15-minute heartbeat
 * staleness window expires (seen 3× in dev, see docs/features/targets.md §13).
 * Refusing here is the only cheap place to stop it.
 *
 * Sniffing the gzip magic rather than trusting the extension is deliberate — a plain
 * file renamed `.ndjson.gz` is exactly the first of those three incidents.
 */
export async function prepareImportBody(file: File, category: string, marques?: TargetRow[]): Promise<{ body: Uint8Array; warnings: string[] }> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || file.type === 'text/csv') {
    return csvToNdjson(file, category, marques); // already gzipped, may carry warnings
  }
  const raw = new Uint8Array(await file.arrayBuffer());
  if (isGzipped(raw)) return { body: raw, warnings: [] };
  if (name.endsWith('.gz')) throw new ImportError('cibles.import.error_not_gzip');
  if (name.endsWith('.ndjson')) return { body: await gzipBytes(raw), warnings: [] };
  throw new ImportError('cibles.import.error_unsupported');
}

// ---------------------------------------------------------------------------
// Group types for catalogue CSV
// ---------------------------------------------------------------------------

export type ProduitGroup = {
  id: string;           // product id (GTIN / SKU)
  name: string;
  urls: { url: string; buyingPage: boolean }[];
  tags: string[];
  parentTargetId: string;
};

export type CollectionGroup = {
  id: string;           // explicit id (if provided) or normalised URL (fallback merge key)
  url: string;
  name: string;
  tags: string[];
  parentTargetId: string;
};

// ---------------------------------------------------------------------------
// CSV helpers — shared by parseCatalogueCsv and csvToNdjson
// ---------------------------------------------------------------------------

/** Detect the field delimiter from the first header line. */
export function _sniffCsvDelimiter(firstLine: string): string {
  return firstLine.includes(';') && !firstLine.includes(',') ? ';' : ',';
}

/** Simple CSV field splitter with quoted-field support. */
export function _parseCsvLine(line: string, delim: string): string[] {
  const cols: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { inQ = !inQ; continue; }
    if (c === delim && !inQ) { cols.push(cur.trim()); cur = ''; continue; }
    cur += c;
  }
  cols.push(cur.trim());
  return cols;
}

/**
 * Parse a unified Catalogue CSV (produit + collection in one file).
 *
 * Mandatory columns: type (Product|Category), url, marque_label.
 * For Product rows: id is also mandatory.
 * Optional: id (Category), name, buying_page (Product only), tags.
 *
 * Returns separate lists so each category can run its own merge pass.
 */
export async function parseCatalogueCsv(
  file: File,
  marques: TargetRow[],
): Promise<{
  produits: ProduitGroup[];
  collections: CollectionGroup[];
  errors: string[];
  warnings: string[];
}> {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.startsWith('#'));
  if (lines.length < 2) throw new ImportError('cibles.import.error_csv_empty');

  const delim = _sniffCsvDelimiter(lines[0]);
  const parseLine = (line: string) => _parseCsvLine(line, delim);

  const headers = parseLine(lines[0]).map((h) =>
    h.toLowerCase().replace(/\s*\(.*?\)/g, '').trim().replace(/\s+/g, '_'),
  );

  const errors: string[] = [];
  const warnings: string[] = [];
  // Deduplicated by `<parentTargetId>::<id>`
  const produitMap  = new Map<string, ProduitGroup>();
  const collectionMap = new Map<string, CollectionGroup>();

  // Detect whether the file has a `type` column. If absent, assume 'product' (backward compat).
  const hasTypeCol = headers.includes('type');

  const parseTags = (raw: string, rowNum: number, label: string): string[] => {
    const out: string[] = [];
    for (const t of raw.split(',').map((s) => s.trim()).filter(Boolean)) {
      if (!/^[a-zA-Z0-9 ]+$/.test(t)) errors.push(`ligne ${rowNum} (${label}) : tag invalide « ${t} »`);
      else if (t.length > TARGET_LIMITS.maxTagLength) errors.push(`ligne ${rowNum} (${label}) : tag trop long « ${t} »`);
      else out.push(t);
    }
    return out.slice(0, TARGET_LIMITS.maxTagsPerTarget);
  };

  for (let i = 1; i < lines.length; i++) {
    const vals = parseLine(lines[i]);
    const fm: Record<string, string> = {};
    headers.forEach((h, idx) => { if (vals[idx]) fm[h] = vals[idx]; });
    const rowNum = i + 1;

    // type — mandatory when column exists, else default 'product'
    const rawType = hasTypeCol ? (fm['type'] || '').trim().toLowerCase() : 'product';
    if (hasTypeCol && !fm['type']?.trim()) {
      errors.push(`ligne ${rowNum} : colonne 'type' obligatoire (Product ou Category)`); continue;
    }
    const isProduct = rawType === 'product' || rawType === 'produit';
    const isCategory = rawType === 'category' || rawType === 'categorie' || rawType === 'catégorie' || rawType === 'catalogue';
    if (!isProduct && !isCategory) {
      errors.push(`ligne ${rowNum} : type « ${fm['type']} » invalide — attendu Product ou Category`); continue;
    }

    // url — mandatory for both
    const rawUrl = fm['url']?.trim();
    if (!rawUrl) { errors.push(`ligne ${rowNum} : colonne 'url' obligatoire`); continue; }
    const { value: normUrl, valid: urlOk } = normalizeProductUrl(rawUrl);
    if (!urlOk) { errors.push(`ligne ${rowNum} : URL invalide « ${rawUrl} »`); continue; }

    // marque_label — mandatory for both
    const marqueLabel = (fm['marque_label'] || fm['brand_label'] || '').trim();
    if (!marqueLabel) { errors.push(`ligne ${rowNum} : colonne 'marque_label' obligatoire`); continue; }
    const marque = marques.find((m) => m.label.trim().toLowerCase() === marqueLabel.toLowerCase());
    if (!marque) { errors.push(`ligne ${rowNum} : marque « ${marqueLabel} » introuvable`); continue; }

    const name = fm['name']?.trim() || '';

    if (isProduct) {
      // id — mandatory for products
      let id = fm['id']?.trim() ?? '';
      if (!id) { errors.push(`ligne ${rowNum} : colonne 'id' obligatoire pour les produits`); continue; }
      if (/^[0-9.]+[eE][+\-]?[0-9]+$/.test(id)) id = String(Math.round(parseFloat(id)));

      const bpRaw = (fm['buying_page'] || '').trim().toLowerCase();
      if (bpRaw && bpRaw !== 'true' && bpRaw !== 'false') {
        errors.push(`ligne ${rowNum} (${id}) : buying_page doit être TRUE ou FALSE`); continue;
      }
      const buyingPage = bpRaw === 'true';
      const tagsRaw = parseTags(fm['tags'] || '', rowNum, id);

      const key = `${marque.targetId}::${id}`;
      if (!produitMap.has(key)) produitMap.set(key, { id, name, urls: [], tags: tagsRaw, parentTargetId: marque.targetId });
      const g = produitMap.get(key)!;
      if (g.urls.length >= TARGET_LIMITS.maxProduitUrls) {
        if (g.urls.length === TARGET_LIMITS.maxProduitUrls)
          warnings.push(`Produit ${id} : limite ${TARGET_LIMITS.maxProduitUrls} URLs atteinte — URL ignorée`);
      } else {
        g.urls.push({ url: normUrl, buyingPage });
      }
      if (tagsRaw.length && !g.tags.length) g.tags = tagsRaw;

    } else {
      // Category: id is optional — normalised URL serves as fallback merge key
      let id = fm['id']?.trim() || normUrl;
      if (/^[0-9.]+[eE][+\-]?[0-9]+$/.test(id)) id = String(Math.round(parseFloat(id)));
      const tagsRaw = parseTags(fm['tags'] || '', rowNum, id);

      const key = `${marque.targetId}::${id}`;
      if (!collectionMap.has(key))
        collectionMap.set(key, { id: fm['id']?.trim() || '', url: normUrl, name, tags: tagsRaw, parentTargetId: marque.targetId });
    }
  }

  if (!produitMap.size && !collectionMap.size && !errors.length)
    throw new ImportError('cibles.import.error_csv_no_rows');
  return { produits: [...produitMap.values()], collections: [...collectionMap.values()], errors, warnings };
}

// Column names → canonical field names.  Handles both snake_case CSV headers and
// camelCase, and several common alternates (brand_label, place_id, …).
const _CSV_FIELD_MAP: Record<string, string> = {
  full_name: 'fullName', fullname: 'fullName',
  first_name: 'firstName', firstname: 'firstName',
  last_name: 'lastName', lastname: 'lastName',
  place_id: 'placeId', placeid: 'placeId',
  // Pass-through fields — key === canonical name, listed for the allow-list check below.
  url: 'url', name: 'name', alias: 'alias', domain: 'domain',
  email: 'email', phone: 'phone', title: 'title',
  code: 'code', id: 'id', zone: 'zone',
  // Legacy fields still accepted (no signal, stored for display)
  sku: 'sku', gtin: 'gtin',
};

// Mandatory fields per category — at least one of these must be non-empty.
const _MANDATORY: Record<string, { fields: string[]; error: string }[]> = {
  personne:      [{ fields: ['lastName', 'last_name', 'lastname'], error: "colonne 'lastName' obligatoire" }],
  etablissement: [{ fields: ['zone'],                              error: "colonne 'zone' obligatoire" },
                  { fields: ['name'],                              error: "colonne 'name' obligatoire" }],
  contenu:       [{ fields: ['url'],                               error: "colonne 'url' obligatoire" }],
  marque:        [{ fields: ['name', 'alias', 'domain'],           error: "au moins un champ parmi 'name', 'alias', 'domain' obligatoire" }],
};

/** Max data rows in a CSV import (= synchronous batch limit, TARGET_LIMITS.maxBatchRows). */
const CSV_MAX_ROWS = 5_000;

/**
 * Parse a CSV file (first row = headers) and return NDJSON bytes ready to upload.
 *
 * Recognised categories: marque, contenu, etablissement, personne.
 * (produit/collection use parseCatalogueCsv + direct POST — not this path.)
 *
 * Mandatory fields, max-row count, URL validity, and field-name normalisation are
 * all checked here so the user gets actionable errors before anything is uploaded.
 */
export async function csvToNdjson(file: File, category: string, marques?: TargetRow[]): Promise<{ body: Uint8Array; warnings: string[] }> {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.startsWith('#'));
  if (lines.length < 2) throw new ImportError('cibles.import.error_csv_empty');
  if (lines.length - 1 > CSV_MAX_ROWS) throw new ImportError('cibles.import.error_too_many_rows');

  const delim = _sniffCsvDelimiter(lines[0]);
  const parseLine = (line: string) => _parseCsvLine(line, delim);

  // Strip annotations like "(obligatoire)" or "(required)" from header names.
  const rawHeaders = parseLine(lines[0]).map((h) =>
    h.toLowerCase().replace(/\s*\(.*?\)/g, '').trim().replace(/\s+/g, '_'),
  );
  const rows: string[] = [];
  const csvErrors: string[] = [];

  for (let i = 1; i < lines.length; i++) {
    const vals = parseLine(lines[i]);
    const fm: Record<string, string> = {};
    rawHeaders.forEach((h, idx) => { if (vals[idx]?.trim()) fm[h] = vals[idx].trim(); });
    const rowNum = i + 1;

    // ── Mandatory field checks ────────────────────────────────────────────────
    for (const rule of _MANDATORY[category] ?? []) {
      const present = rule.fields.some((f) => fm[f]?.trim());
      if (!present) { csvErrors.push(`ligne ${rowNum} : ${rule.error}`); }
    }
    if (csvErrors.length > 0 && csvErrors[csvErrors.length - 1].startsWith(`ligne ${rowNum}`)) continue;

    // ── Resolve marque_label → parentTargetId ────────────────────────────────
    const marqueLabel = (fm['marque_label'] || fm['brand_label'] || '').trim();
    const parentMarque = marqueLabel && marques
      ? marques.find((m) => m.label.trim().toLowerCase() === marqueLabel.toLowerCase())
      : undefined;

    // ── Build fields list ─────────────────────────────────────────────────────
    const fields: { name: string; value: any }[] = [];
    for (const [rawKey, val] of Object.entries(fm)) {
      if (rawKey === 'marque_label' || rawKey === 'brand_label') continue;

      const fieldName = _CSV_FIELD_MAP[rawKey];
      if (!fieldName) continue; // unknown column — skip silently

      if (fieldName === 'zone') {
        // zone: keep the raw label; geocoding resolves coordinates server-side.
        // countryCode defaults to '' — the Python worker will try to infer it from
        // the label, but an explicit "Paris, France" is more reliable than hardcoding FR.
        fields.push({ name: 'zone', value: { label: val, type: 'locality', countryCode: '', geocodeStatus: 'pending' } });
        continue;
      }

      if (fieldName === 'url' || fieldName === 'domain') {
        const { value: normVal, valid } = normalizeDomainInput(val);
        if (!valid) {
          csvErrors.push(`ligne ${rowNum} : URL invalide « ${val} »`);
          continue;
        }
        fields.push({ name: fieldName, value: normVal });
        continue;
      }

      fields.push({ name: fieldName, value: val });
    }

    const label = fm['name'] || fm['full_name'] || fm['fullname'] || fm['last_name'] || fm['lastname'] || fm['lastName'] || fm['url'] || `row-${i}`;
    const target: any = { ownerId: 'own', category, label, fields };
    if (parentMarque) target.parentTargetId = parentMarque.targetId;
    rows.push(JSON.stringify(target));
  }

  if (csvErrors.length) {
    const shown = csvErrors.slice(0, 10);
    const extra = csvErrors.length > 10 ? `\n… et ${csvErrors.length - 10} autre(s) erreur(s)` : '';
    throw new ImportError(shown.join('\n') + extra);
  }

  if (!rows.length) throw new ImportError('cibles.import.error_csv_no_rows');

  return { body: await gzipBytes(new TextEncoder().encode(rows.join('\n'))), warnings: [] };
}

/**
 * Normalize a domain/URL input typed by the user.
 * - Strip https://, http://
 * - Strip leading www.
 * - Remove .html / .htm / .php / .asp page extensions
 * - Remove query string and fragment
 * - Keep the path if present (acme.fr/blog is valid)
 * - Return null if what remains doesn't look like a valid domain
 */
export function normalizeDomainInput(raw: string): { value: string; valid: boolean } {
  let v = raw.trim();
  if (!v) return { value: v, valid: false };
  // Strip scheme
  v = v.replace(/^https?:\/\//, '');
  // Strip www.
  v = v.replace(/^www\./, '');
  // Remove fragment and query string
  v = v.split('#')[0].split('?')[0];
  // Remove file extension at end of path
  v = v.replace(/\.(html?|php|asp[x]?|jsp)$/i, '');
  // Remove trailing slash
  v = v.replace(/\/$/, '');
  // Validate: must have a dot somewhere in the host part
  const host = v.split('/')[0];
  const valid = /^[a-zA-Z0-9][a-zA-Z0-9\-]*(\.[a-zA-Z0-9\-]+)+$/.test(host);
  return { value: v, valid };
}

/**
 * Minimal normalization for product page URLs.
 * Only strips scheme and www. — keeps path, query string, and file extension intact
 * because product URLs with query params (`?variant=red`) or paths like `/p/item.html`
 * are distinct identifiers and must not be truncated.
 */
export function normalizeProductUrl(raw: string): { value: string; valid: boolean } {
  let v = raw.trim();
  if (!v) return { value: v, valid: false };
  v = v.replace(/^https?:\/\//, '');
  v = v.replace(/^www\./, '');
  // Fragment is never a signal (not transmitted by browsers) — strip it.
  v = v.split('#')[0];
  // Remove trailing slash only when there's no path after it.
  if (v.endsWith('/') && !v.includes('?')) v = v.replace(/\/$/, '');
  // Must have a dot in the host part (basic domain check).
  const host = v.split('/')[0].split('?')[0];
  const valid = host.includes('.') && host.length > 2;
  return { value: v, valid };
}

export function downloadCsv(filename: string, content: string) {
  // BOM ensures Excel opens the file in UTF-8 (accented characters, etc.)
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
